<script setup lang="ts">
import { SharedData } from '@/@types'
import { Coupon } from '@/@types/cart'
import { Order, OrdersnapShot } from '@/@types/order'
import { PaymentHeader, PaymentTitle } from '@/components/shared/checkout/payment-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { formatPrice } from '@/helpers/current-price'
import { Link, router, usePage, usePoll } from '@inertiajs/vue3'

import Resume from '../cart/payment-methods/Resume.vue'
import PaymentStepper from './payment-header/PaymentStepper.vue'

interface OrderWithCoupon extends Order {
    coupon: Coupon
    order_snap_shot: OrdersnapShot
}

const { order } = defineProps<{ order: OrderWithCoupon }>()

const { user } = usePage<SharedData>().props

const { stop } = usePoll(15000, {
    only: ['order'],
    onFinish: () => {
        if (order.status !== 'pending') {
            stop()
            router.visit(`/orders/${order.id}`, {
                only: ['order'],
            })
        }
    },
})
</script>

<template>
    <main class="container" v-if="order.transaction_detail">
        <PaymentHeader :variant="order.status">
            <PaymentTitle :variant="order.status">
                {{ $t('Order received') }}
                <template #description>
                    {{ $t('Your order') }} <strong>#{{ order.id }}</strong> {{ $t('was placed successfully') }}.</template
                >
            </PaymentTitle>
            <PaymentStepper
                :steps="[
                    {
                        name: $t('Order completed'),
                        state: 'completed',
                    },
                    { name: $t('Payment received'), state: 'inactive' },
                ]"
            />
        </PaymentHeader>

        <section class="mt-6 flex flex-col gap-8 lg:flex-row">
            <div class="flex flex-1 flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-8">
                <div class="mx-auto flex max-w-xl flex-col items-center justify-center">
                    <Badge
                        :variant="order.status"
                        class="rounded-sm border-0 before:size-1.5 before:rounded-full before:bg-yellow-400 before:content-['']"
                    >
                        {{ $t(order.status) }}
                    </Badge>

                    <h2 class="mt-4 mb-2.5 text-center font-sans text-2xl font-semibold">
                        {{ $t('Amount paid:', { amount: formatPrice(order.transaction_detail.amount / 100) }) }}
                    </h2>

                    <p
                        class="text-center text-[#444]"
                        v-html="
                            $t(
                                `Thank you for purchasing from ILOS! A summary of your order has been sent to  with information about the tickets purchased and the invoice for your order.`,
                                { email: user?.email },
                            )
                        "
                    />
                </div>

                <Separator class="mx-auto my-8 max-w-[612px]" />

                <div class="mb-8 w-full bg-gray-50 p-4 text-sm">
                    <table>
                        <tbody class="">
                            <tr>
                                <td class="w-[120px] py-1 sm:w-[190px]">{{ $t('Name') }}:</td>
                                <td class="font-medium">{{ order.transaction_detail.card_info.holder_name }}</td>
                            </tr>
                            <tr>
                                <td class="w-[120px] py-1 sm:w-[190px]">{{ $t('Order number') }}:</td>
                                <td class="font-medium">#{{ order.id }}</td>
                            </tr>
                            <tr>
                                <td class="w-[120px] py-1 sm:w-[190px]">{{ $t('Total paid') }}:</td>
                                <td class="font-medium">{{ formatPrice(order.transaction_detail.amount / 100) }}</td>
                            </tr>
                            <tr>
                                <td class="w-[120px] py-1 sm:w-[190px]">{{ $t('Payment method') }}:</td>
                                <td class="font-medium">{{ $t(order.payment_type) }}</td>
                            </tr>
                            <tr>
                                <td class="w-[120px] py-1 sm:w-[190px]">{{ $t('Card') }}:</td>
                                <td class="font-medium">
                                    <span class="uppercase">{{ order.transaction_detail.card_info.brand }}</span>
                                    **** {{ order.transaction_detail.card_info.last_digits }}
                                </td>
                            </tr>
                            <tr>
                                <td class="w-[120px] py-1 sm:w-[190px]">{{ $t('Number of installments') }}:</td>
                                <td class="font-medium">{{ order.transaction_detail.installments }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Button :as="Link" href="?show_order_details=true">{{ $t('View order details') }}</Button>
            </div>
            <Resume :order="order" />
        </section>
    </main>
</template>
